import { CallLambda } from "redux/actions/CallLambda";
import { SaveObjectAction } from "redux/actions/SaveObject";
import Config from "redux/classes/Config";
import Model from "redux/classes/Model";
import Session from "redux/classes/Session";
import { AppDispatch } from "redux/store";
import { parse } from "papaparse";

export type JobFeedback = Readonly<{
    rating: "up" | "down";
    comment: string;
}>;

export type JobInfo = Readonly<{
    jobID: string;
    alias: string;
    question: string;
    startTime: number;
    endTime?: number;
    status: string;
    statusMessage?: string;
    feedback?: JobFeedback;
    tags: string[];
    replyTarget?: string;
    scheduleTime?: string;
    scheduleType?: string;
}>;

export type JobState = Readonly<{
    jobID: string;
    status: string;
    alias: string;
    statusMessage?: string;
    endTime?: number;
}>;

export type JobOutputItem<TContent> = Readonly<{
  name: string;
  extension: string;
  content: TContent;
}>

export type JobOutput = Readonly<{
    Response?: JobOutputItem<string>;
    Data?: JobOutputItem<JobOutputData>;
    Conversation?: JobOutputItem<any>;
    Error?: JobOutputItem<string>;
    ResponsePdf?: JobOutputItem<string>;
}>;

export type JobOutputData = Readonly<{
    columns: { title: string, dataIndex: string, key: string, align: "left" | "right" | "center" }[];
    dataSource: { key: string, [dataIndex: string]: string }[];
}>;

export type JobOutputPaths = Readonly<{
  Response?: string;
  Data?: string;
  Error?: string;
  ResponsePdf?: string;
}>;

export type LoadJobsResult = Readonly<{
    serverTime: number;
    jobs: readonly JobInfo[];
}>;

export type LoadJobStatesResult = Readonly<{
    serverTime: number;
    jobStates: readonly JobState[];
}>;

export type JobFeedbackItem = Readonly<{
  jobID: string;
  jobStartTime: string;
  feedbackTime: string;
  userID: string;
  question: string;
  rating: "up"|"down";
  comment: string;
  outputs: JobOutputPaths;
}>;

export type LoadJobFeedbackResult = Readonly<{
    cursor?: any;
    items: ReadonlyArray<JobFeedbackItem>;
}>;

export class NLIJobService {

    constructor(
      public dispatch: AppDispatch,
      public session: Session,
      public model: Model,
      public userID: string) {

    }

    async loadJobs(): Promise<LoadJobsResult> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJobs", modelID: this.model.modelID }));
      if (status !== 200) {
          throw new Error("Failed loading previous questions");
      }
      return {
          serverTime: Date.parse(data.serverTime),
          jobs: data.jobs.map((job: any) => ({
              jobID: job.job_id,
              alias: job.alias,
              question: job.description,
              startTime: Date.parse(job.start_time),
              endTime: job.end_time ? Date.parse(job.end_time) : undefined,
              status: job.job_status,
              statusMessage: job.running_message || "",
              feedback: job.feedback,
              tags: job.tags ? Object.values(job.tags) : [],
              replyTarget: job.reply_target,
              scheduleTime: job.schedule_time || undefined,
              scheduleType: job.schedule_type || undefined
          })).sort((a: JobInfo, b: JobInfo) => b.startTime - a.startTime)
      };
    }

    async loadJobStates(): Promise<LoadJobStatesResult> {
      // Light polling Query — returns the polling-relevant fields for every
      // in-scope job (cust_id + model_id + user_id). The caller diffs the
      // returned IDs against its in-memory list to detect new jobs and
      // deletions, and follows up with loadJobsByIDs for any new IDs.
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", {
        action: "loadJobStates",
        modelID: this.model.modelID,
      }));
      if (status !== 200) {
          throw new Error("Failed refreshing job information");
      };
      return {
        serverTime: Date.parse(data.serverTime),
        jobStates: data.jobStates.map((jobState: any) => ({
          jobID: jobState.job_id,
          status: jobState.job_status,
          statusMessage: jobState.running_message,
          endTime: jobState.end_time ? Date.parse(jobState.end_time) : undefined,
          alias: jobState.alias
        }))
      }
    }

    async loadJobsByIDs(jobIDs: readonly string[]): Promise<LoadJobsResult> {
      // Targeted full-row fetch for IDs the client doesn't yet have. Used as
      // a follow-up to loadJobStates when new IDs are surfaced. Returns the
      // same JobInfo shape as loadJobs.
      if (jobIDs.length === 0) {
        return { serverTime: Date.now(), jobs: [] };
      }
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", {
        action: "loadJobsByIDs",
        jobIDs: jobIDs,
      }));
      if (status !== 200) {
        throw new Error("Failed loading new jobs");
      }
      return {
        serverTime: Date.parse(data.serverTime),
        jobs: data.jobs.map((job: any) => ({
          jobID: job.job_id,
          alias: job.alias,
          question: job.description,
          startTime: Date.parse(job.start_time),
          endTime: job.end_time ? Date.parse(job.end_time) : undefined,
          status: job.job_status,
          statusMessage: job.running_message || "",
          feedback: job.feedback,
          tags: job.tags ? Object.values(job.tags) : [],
          replyTarget: job.reply_target,
          scheduleTime: job.schedule_time || undefined,
          scheduleType: job.schedule_type || undefined
        }))
      };
    }

    async loadJobOutput(jobID: string, cachedOutputPaths?: JobOutputPaths): Promise<JobOutput> {

      let outputPaths: {[key: string]: string};
      if (cachedOutputPaths) {
        outputPaths = cachedOutputPaths;
      } else {
        const payload = JSON.stringify({
          "model": this.model.alias,
          "jobID": jobID,
        })

        const {status, data} = await this.dispatch(CallLambda(this.session, "jobLoader", payload))

        if (status !== 200) {
          throw new Error("Failed loading job details");
        }

        const outputs = JSON.parse(data.config).outputs;
        const errorPath = {
          'Error': 'error.err'
        }
        outputPaths = { ...outputs, ...errorPath };
      }

      const payload = JSON.stringify({
        "model": this.model.alias,
        "jobID": jobID,
        "outputs": Object.values(outputPaths),
        "status": "REVIEW"
      });

      const {status, data} = await this.dispatch(CallLambda(this.session, "resultLoader", payload));

      if (status !== 200) {
        throw new Error("Failed loading job results");
      }

      const results = data.results;

      const jobOutput: { Data?: JobOutputItem<JobOutputData>, Response?: JobOutputItem<string>, Conversation?: JobOutputItem<any>, Error?: JobOutputItem<string>, ResponsePdf?: JobOutputItem<string> } = {};
      for (let path in results) {
          let parts = path.split(".");
          let name = parts.slice(0, parts.length - 1).join(".");
          let ext = parts[parts.length - 1];
          const label = Object.keys(outputPaths).find(k => outputPaths[k] === path) as string;

          let data: any;

          try {
            if (label === "Response PDF") {
              // For PDF, store the URL directly without fetching binary content
              const url = atob(results[path]);
              jobOutput.ResponsePdf = {
                name: name,
                extension: ext,
                content: url
              };
              continue;
            }
            if (label !== "Error") {
              const url = atob(results[path]);
              const response = await fetch(url);
              if (!response.ok) throw new Error('Failed to fetch content');
              data = (await response.text()).trim();
              if (label === "Data") {
                const parsed = parse(data as string, { header: false });
                data = parsed.data as string[][];
              }
            }
            else {
              data = atob(results[path]).trim();
            }
          } catch (error) {
            console.error('Error fetching content:', error);
            continue;
          }

          if (label === "SQL" || label === "Response") {
            jobOutput.Response = {
              name: name,
              extension: ext,
              content: data
            }
          }
          if (label === "Conversation") {
            try {
              // Parse the conversation JSON to access fields like follow_up_question
              const conversationData = JSON.parse(data);
              jobOutput.Conversation = {
                name: name,
                extension: ext,
                content: conversationData
              }
            } catch (error) {
              // If parsing fails, store as boolean flag (backward compatibility)
              console.error('Error parsing conversation JSON:', error);
              jobOutput.Conversation = {
                name: name,
                extension: ext,
                content: true
              }
            }
          }
          if (label === "Error") {
            jobOutput.Error = {
              name: name,
              extension: ext,
              content: data
            }
          }
          if (label === "Data") {
            const dataRows = data.slice(1).map((row: string[]) => row.map((cell: string) => cell));

            const columnMetadata = data[0].map(() => ({
              type: "text",
              maxDecimals: 0
            })) as Array<{type: "text"|"number", maxDecimals: number}>;

            const columns = data[0].map((header: string, colIndex: number) => {
              let colName = header || "";
              const prefix = colName.substring(0,1);
              let align = "left";
              let maxDecimals = 0;
              switch (prefix) {
                case "#":
                  columnMetadata[colIndex].type = "number";
                  align = "right";
                  colName = colName.substring(1);
                  dataRows.forEach((row: string[]) => {
                    const s = row[colIndex]?.trim();
                    if (s) {
                      let decimals = 0;
                      const dotIndex = s.indexOf(".");
                      if (dotIndex >= 0) {
                        decimals = s.length - dotIndex - 1;
                        if (decimals > maxDecimals) {
                          maxDecimals = decimals;
                        }
                      }
                    }
                  });
                  columnMetadata[colIndex].maxDecimals = maxDecimals;
                  break;
                case "$":
                case "&":
                case "%":
                  colName = colName.substring(1);
                  break;
              }
              return {
                title: colName,
                dataIndex: colIndex.toString(),
                key: colIndex.toString(),
                align: align
              };
            });

            jobOutput.Data = {
              name: name,
              extension: ext,
              content: {
                columns: columns,
                dataSource: dataRows.map((row: string[], rowIndex: number) => {
                  const dataItem: {[key: string]: string} = {
                    key: "row" + rowIndex
                  };
                  data[0].forEach((column: string, colIndex: number) => {
                    let textValue = row[colIndex];
                    const meta = columnMetadata[colIndex];
                    if (meta.type === "number") {
                      const n = parseFloat(textValue);
                      if (!isNaN(n)) {
                        const maximumFractionDigits = 6;
                        const minimumFractionDigits = Math.min(meta.maxDecimals, maximumFractionDigits);
                        textValue = n.toLocaleString(undefined, {minimumFractionDigits, maximumFractionDigits: minimumFractionDigits});
                      }
                    }
                    dataItem[colIndex.toString()] = textValue;
                  });
                  return dataItem;
                })
              }
            };
          }
      }
      return jobOutput;
    }

    async createJob(modelAlias: string, question: string, conversationID: string = '', conversationQuestion: string = '', memory: string, alias?: string, tags?: string[], email?: string, jobID?: string): Promise<JobInfo> {

      let date = new Date()
      let newJobID = jobID || date.toISOString()
      let jobAlias = alias || `${this.model.alias}-${newJobID}`

      let config = new Config(
        {
          "userID": this.userID,
          "alias": jobAlias,
          "description": question,
          ...(email ? { "email": email } : {})
        },
        {
          "Request": question,
          ...(conversationID ? { "Reply Target": modelAlias + "-" + conversationID } : {})
        },
        {
          "System Prompt": "prompt/" + modelAlias + ".txt",
          "Build SQL": "sql/" + modelAlias + ".sql",
          "Data Dictionary": "dictionary/" + modelAlias + ".csv",
          "Tactic Activity": "tactics/" + modelAlias + ".csv",
          "ADL Run Stats": "refresh/" + modelAlias + ".csv"
        },
        {
          "SQL": "generated_query.md",
          "Conversation": "conversation.json"
        },
      );

      if (modelAlias.startsWith("QST_") || modelAlias.startsWith("QUERYSTREAM_")) {
        config = new Config(
          {
            "userID": this.userID,
            "alias": alias || "New Stream",
            "description": question,
            ...(email ? { "email": email } : {})
          },
          {
            "Memory": memory,
            "Request": question,
            ...(conversationID ? { "Reply Target": modelAlias + "-" + conversationID } : {})
          },
          {
            "Data Dictionary": "dictionary/" + modelAlias + ".csv",
            "System Prompt": "prompt/system/" + modelAlias + ".txt",
            "Context Instructions": "prompt/context/" + modelAlias + ".txt",
            "Conversation Name Prompt": "prompt/name/" + modelAlias + ".txt",
            "Follow-up Question Prompt": "prompt/follow-up/" + modelAlias + ".txt",
            ...(modelAlias.startsWith("QST_MSBU") || modelAlias.startsWith("QST_ONC") || modelAlias.startsWith("QST_OPH") || modelAlias.startsWith("QST_MA") || modelAlias.startsWith("QST_COMM") ? {
              "Build SQL": "sql/" + modelAlias + ".sql",
              ...(!modelAlias.startsWith("QST_MA") && !modelAlias.startsWith("QST_COMM") ? {
                "Tactic Activity": "tactics/" + modelAlias + ".csv",
                "ADL Run Stats": "refresh/" + modelAlias + ".csv"
              } : {})
            } : {}),
            ...(modelAlias.startsWith("QUERYSTREAM_QC") ? {
              "Build SQL": "sql/" + modelAlias + ".zip"
            } : {})
          },
          {
            "Data": "data.csv",
            "Response": "response.md",
            "Response PDF": "response.pdf",
            "Conversation": "conversation.json"
          }
        );
      }

      const configJSON = config.makeJSON();

      // Always upload config to S3 to avoid CloudFront/API Gateway size limits
      const configBlob = new Blob([configJSON], { type: 'application/json' });
      const configBaseName = `${this.model.alias}_config_${newJobID}.json`;
      const configRelativePath = `config/${configBaseName}`;

      // Get presigned URL for config upload
      const uploadPayload = JSON.stringify({
        "model": this.model.alias,
        "file": configBaseName,
        "size": configBlob.size.toString(),
        "type": "application/json",
        "target": "config"
      });

      const uploadResponse = await this.dispatch(CallLambda(this.session, "fileUploader", uploadPayload));

      if (uploadResponse.status !== 200) {
        throw new Error("Failed to get presigned URL for config upload");
      }

      const presignedUrl = uploadResponse.data.presignedURL;
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        body: configBlob,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!uploadResult.ok) {
        throw new Error("Failed to upload config to S3");
      }

      const finalPayload = JSON.stringify({
        "model": this.model.alias,
        "configS3Path": configRelativePath,
        "jobID": newJobID,
      });

      const {status, data} = await this.dispatch(CallLambda(this.session, "jobCreator", finalPayload))

      if (status !== 200) {
        throw new Error("Failed creating job");
      }
      const payload2 = JSON.stringify({
        "model": this.model.alias,
        "jobID": newJobID,
        "jobAlias": ((modelAlias.startsWith("QST_") || modelAlias.startsWith("QUERYSTREAM_")) && !alias) ? "New Stream" : jobAlias,
        "jobDesc": question.substring(0, 200),
        "modelID": this.model.modelID,
        "launchType": this.model.launchType,
        "replyTarget": conversationID ? modelAlias + "-" + conversationID : undefined
      })

      const response = await this.dispatch(CallLambda(this.session, "modelInitializer", payload2))

      if (response.status !== 200) {
        throw new Error("Failed initializing job");
      };

      // Apply tags if provided (after job is created)
      if (tags && tags.length > 0) {
        try {
          await this.addJobTags(newJobID, tags);
        } catch (error) {
          // Log error but don't fail job creation if tags can't be added
          console.error("Failed to add tags to job:", error);
        }
      }

      const {status: loadStatus, data: loadedData} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJob", jobID: newJobID }));
      if (loadStatus !== 200) {
        throw new Error("Failed loading new job");
      }

      const job = loadedData.job;
      return {
          jobID: job.job_id,
          alias: job.alias,
          question: job.description,
          startTime: Date.parse(job.start_time),
          status: job.job_status,
          statusMessage: job.running_message || "",
          tags: job.tags ? Object.values(job.tags) : [],
          replyTarget: job.reply_target
      };
    }

    async deleteJob(jobID: string): Promise<void> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "jobDeleter", { jobID: jobID }));
      if (status !== 200) {
          throw new Error("Failed deleting job");
      }
    }

    async scheduleJob(jobID: string, scheduleTime: string | null, scheduleType: string | null): Promise<void> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "jobScheduler", {
        jobID: jobID,
        scheduleTime: scheduleTime,
        scheduleType: scheduleType
      }));
      if (status !== 200) {
          throw new Error("Failed scheduling job");
      }
    }

    async deleteJobsByReplyTarget(jobID: string, deleteSourceJob: boolean = false): Promise<void> {
      const replyTarget = `${this.model.alias}-${jobID}`;
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "deleteJobsByReplyTarget", replyTarget: replyTarget, deleteSourceJob: deleteSourceJob }));
      if (status !== 200) {
          throw new Error("Failed deleting jobs by reply target");
      }
    }

    async regenerateJob(jobID: string, question: string, memory: string, replyTarget?: string): Promise<JobInfo> {
      // Fetch the existing job to get its alias, tags, and schedule before deletion
      const {status: loadStatus, data: loadedData} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJob", jobID: jobID }));
      if (loadStatus !== 200) {
          throw new Error("Failed loading job for regeneration");
      }
      const existingJob = loadedData.job;
      const existingAlias = existingJob.alias;
      const existingTags = existingJob.tags ? Object.values(existingJob.tags) as string[] : [];
      const existingScheduleTime = existingJob.schedule_time || null;
      const existingScheduleType = existingJob.schedule_type || null;

      // Delete the job and all its replies (deleteSourceJob=true will also delete the source job)
      await this.deleteJobsByReplyTarget(jobID, true);

      // Extract conversationID from replyTarget if it exists
      let conversationID = '';
      if (replyTarget) {
        // replyTarget format is "modelAlias-jobID", extract the jobID part
        const parts = replyTarget.split('-');
        if (parts.length > 1) {
          conversationID = parts.slice(1).join('-');
        }
      }

      // Create a new job with the same question, replyTarget, alias, and tags. Do NOT
      // pass the old jobID — letting createJob generate a fresh one means the new job's
      // S3 output prefix (s3://.../<modelAlias>/output/<newJobID>/) is empty, so a
      // stale error.err from the failed previous run can't bleed through into the UI.
      // (The Lambda's uploadOutputs only uploads new files; it doesn't delete old ones,
      // so reusing the prefix would surface stale artifacts.)
      const newJobInfo = await this.createJob(this.model.alias, question, conversationID, '', memory, existingAlias, existingTags);

      // Preserve the schedule if it existed
      if (existingScheduleTime) {
          await this.scheduleJob(newJobInfo.jobID, existingScheduleTime, existingScheduleType);
      }

      return newJobInfo;
    }

    download(jobID: string, name: string, extension: string) {
      this.dispatch(SaveObjectAction(this.session, this.model.alias, jobID, name, extension));
    }

    async addJobTags(jobID: string, tags: string[]) {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "addJobTags", jobID: jobID, tags: tags }));
      if (status !== 200) {
          throw new Error("Failed saving tags");
      }
    }

    async removeJobTags(jobID: string, tags: string[]) {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "deleteJobTags", jobID: jobID, tags: tags }));
      if (status !== 200) {
          throw new Error("Failed saving tags");
      }
    }

    async saveJobFeedback(jobID: string, feedback: JobFeedback, outputs: JobOutput) {
      const outputPaths: {[key: string]: string} = {};
      if (outputs.Response) {
        outputPaths["Response"] = outputs.Response.name + "." + outputs.Response.extension;
      }
      if (outputs.Data) {
        outputPaths["Data"] = outputs.Data.name + "." + outputs.Data.extension;
      }
      if (outputs.Error) {
        outputPaths["Error"] = outputs.Error.name + "." + outputs.Error.extension;
      }
      if (outputs.ResponsePdf) {
        outputPaths["ResponsePdf"] = outputs.ResponsePdf.name + "." + outputs.ResponsePdf.extension;
      }
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "saveJobFeedback", jobID: jobID, rating: feedback.rating, comment: feedback.comment, outputs: outputPaths }));
      if (status !== 200) {
          throw new Error("Failed saving feedback");
      }
    }

    async deleteJobFeedback(jobID: string) {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "deleteJobFeedback", jobID: jobID }));
      if (status !== 200) {
          throw new Error(`Failed deleting feedback`);
      }
    }

    async updateJobAlias(jobID: string, alias: string): Promise<void> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "updateJobAlias", jobID: jobID, alias: alias }));
      if (status !== 200) {
          throw new Error("Failed updating job alias");
      }
    }

    async loadJobFeedback(cursor: any, minResults: number): Promise<LoadJobFeedbackResult> {
      const {status, data} = await this.dispatch(CallLambda(this.session, "nliService", { action: "loadJobFeedback", modelID: this.model.modelID, cursor: cursor, minResults: minResults }));
      if (status !== 200) {
          throw new Error("Failed loading feedback");
      }
      return {
        cursor: data.cursor,
        items: data.items.map((item: any) => ({
          jobID: item.job_id,
          jobStartTime: item.job_start_time,
          feedbackTime: item.feedback_time,
          userID: item.user_id,
          question: item.question,
          rating: item.rating,
          comment: item.comment,
          outputs: item.outputs
        }))
      };
    }
}
